/**
 * Normalizes an email address according to Google Ads Enhanced Conversions requirements
 * - Converts to lowercase
 * - Trims whitespace
 * - Removes dots from Gmail addresses before the @ symbol
 * @param email The email address to normalize
 * @returns The normalized email address
 */
export function normalizeEmail(email: string): string {
  if (!email) return '';
  
  // Convert to lowercase and trim
  let normalized = email.toLowerCase().trim();
  
  // For Gmail addresses, remove dots before the @ symbol
  if (normalized.includes('@gmail.com')) {
    const [localPart, domain] = normalized.split('@');
    const normalizedLocalPart = localPart.replace(/\./g, '');
    normalized = `${normalizedLocalPart}@${domain}`;
  }
  
  return normalized;
}

/**
 * Normalizes a phone number to E.164 format for Google Ads Enhanced Conversions
 * @param phone The phone number to normalize
 * @param countryCode The country code (default: 'AU' for Australia)
 * @returns The normalized phone number in E.164 format
 */
export function normalizePhone(phone: string, countryCode: string = 'AU'): string {
  if (!phone) return '';
  
  // Remove all non-numeric characters
  let digits = phone.replace(/\D/g, '');
  
  // Handle Australian phone numbers
  if (countryCode === 'AU') {
    // Remove leading 0 if present
    if (digits.startsWith('0')) {
      digits = digits.substring(1);
    }
    
    // If already has country code, ensure it's correct
    if (digits.startsWith('61')) {
      return `+${digits}`;
    }
    
    // Add Australian country code
    if (digits.length === 9) {
      return `+61${digits}`;
    }
  }
  
  // Handle US phone numbers
  if (countryCode === 'US') {
    // Remove leading 1 if present
    if (digits.startsWith('1') && digits.length === 11) {
      return `+${digits}`;
    }
    
    // Add US country code
    if (digits.length === 10) {
      return `+1${digits}`;
    }
  }
  
  // Handle Canadian phone numbers (same as US)
  if (countryCode === 'CA') {
    // Remove leading 1 if present
    if (digits.startsWith('1') && digits.length === 11) {
      return `+${digits}`;
    }
    
    // Add Canadian country code
    if (digits.length === 10) {
      return `+1${digits}`;
    }
  }
  
  // Return the digits with a + if we couldn't determine the format
  return digits.length > 0 ? `+${digits}` : '';
}

/**
 * Hashes a value using SHA-256 for Google Ads Enhanced Conversions
 * Uses Web Crypto API for browser compatibility
 * @param value The value to hash
 * @returns Promise of the SHA-256 hash in hexadecimal format
 */
export async function hashValue(value: string): Promise<string> {
  if (!value) return '';
  
  // Convert string to Uint8Array
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  
  // Hash the data
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  
  // Convert to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
}

/**
 * Prepares user data for Google Ads Enhanced Conversions
 * @param email The user's email address
 * @param phone The user's phone number
 * @param countryCode The country code for phone normalization
 * @returns Promise of object with normalized and hashed values
 */
export async function prepareEnhancedConversionData(
  email: string,
  phone: string,
  countryCode: string = 'AU'
): Promise<{
  email: string;
  phone: string;
  hashed_email: string;
  hashed_phone: string;
  normalized_email: string;
  normalized_phone: string;
}> {
  const normalizedEmail = normalizeEmail(email);
  const normalizedPhone = normalizePhone(phone, countryCode);
  
  const [hashedEmail, hashedPhone] = await Promise.all([
    hashValue(normalizedEmail),
    hashValue(normalizedPhone)
  ]);
  
  return {
    email,
    phone,
    hashed_email: hashedEmail,
    hashed_phone: hashedPhone,
    normalized_email: normalizedEmail,
    normalized_phone: normalizedPhone,
  };
}